'use client'

import React, { FC, PropsWithChildren, ReactElement } from 'react'
import classNames from 'classnames'

import Modal from '@/components/modal'
import Button from '@/components/button'
import AlertCircleIcon from '@/icons/alert-circle'
import AlertIcon from '@/icons/alert'
import styles from './WarningModal.module.css'
import ButtonGradientEpic from '@/components/button-gradient-epic'

const WarningModal: FC<
	PropsWithChildren<{
		open: boolean
		onClose: (evt?: any) => void
		title?: string
		description?: ReactElement
		confirmLabel?: string
		onConfirm?: () => void
		cancelLabel?: string
		onCancel?: () => void
		buttonDisplay?: 'default' | 'default-epic' | 'inverted' | 'confirm-only'
		icon?: 'alert' | 'warning'
	}>
> = (props): ReactElement => {
	const { onClose, onConfirm, icon } = props

	const handleClose = React.useCallback(
		(evt: any) => {
			evt?.stopPropagation()
			evt?.preventDefault()

			onClose && onClose()
		},
		[onClose]
	)

	const handleConfirm = React.useCallback(
		(evt: any) => {
			evt?.stopPropagation()
			evt?.preventDefault()

			onConfirm && onConfirm()
		},
		[onConfirm]
	)

	const renderButtons = React.useCallback(() => {
		switch (props.buttonDisplay) {
			case 'confirm-only':
				return (
					<Button
						size="small"
						background="white"
						onClick={handleConfirm}
						className={styles.topButton}
					>
						{props.confirmLabel}
					</Button>
				)
				break
			case 'inverted':
				return (
					<>
						<Button
							size="small"
							background="white"
							onClick={handleClose}
							className={styles.topButton}
						>
							{props.cancelLabel}
						</Button>
						<Button
							size="small"
							background="transparent"
							className={styles.bottomButton}
							onClick={handleConfirm}
						>
							{props.confirmLabel}
						</Button>
					</>
				)
				break
			case 'default-epic':
				return (
					<>
						<ButtonGradientEpic onClick={handleConfirm} className={styles.topButton}>
							{props.confirmLabel}
						</ButtonGradientEpic>
						<Button
							size="small"
							background="transparent"
							className={styles.bottomButton}
							onClick={handleClose}
						>
							{props.cancelLabel}
						</Button>
					</>
				)
				break
			case 'default':
			default:
				return (
					<>
						<Button
							size="small"
							background="white"
							onClick={handleConfirm}
							className={styles.topButton}
						>
							{props.confirmLabel}
						</Button>
						<Button
							size="small"
							background="white"
							className={styles.bottomButton}
							onClick={handleClose}
						>
							{props.cancelLabel}
						</Button>
					</>
				)
				break
		}
	}, [props, handleClose, handleConfirm])

	return (
		<Modal open={props.open} onClose={handleClose}>
			<div className={styles.root}>
				{icon === 'alert' ? (
					<AlertCircleIcon className={styles.icon} />
				) : (
					<AlertIcon className={styles.icon} />
				)}
				<p className={classNames(styles.title, 'text-lg', 'semibold')}>{props.title}</p>
				<p className={classNames(styles.description, 'text-sm', 'regular')}>{props.description}</p>
				{props.children}
				<div className={styles.actions}>{renderButtons()}</div>
			</div>
		</Modal>
	)
}

export default WarningModal
